import { type FC, useCallback, useState } from "react";
import { type MessageType } from "./Messages/Message";
import classNames from "classnames";
import { Box, IconButton, styled } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export interface Props {
  msgType: MessageType;
  msgCount: number;
  onScrollToMessage: (index: number) => void;
  sticky?: boolean;
  isBulkUploadPreview?: boolean;
}

const CheckMessagesComponent: FC<Props & JSX.IntrinsicElements["div"]> = (props) => {
  const { msgType, className, onScrollToMessage, msgCount, sticky, isBulkUploadPreview, ...divProps } = props;
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(-1);

  const nextClick = useCallback(() => {
    let nextIndex = activeIndex + 1;
    if (nextIndex >= msgCount) {
      nextIndex = 0;
    }
    setActiveIndex(nextIndex);
    onScrollToMessage(nextIndex);
  }, [activeIndex, msgCount, onScrollToMessage]);

  const prevClick = useCallback(() => {
    let nextIndex = activeIndex - 1;
    if (nextIndex < 0) {
      nextIndex = msgCount - 1;
    }
    setActiveIndex(nextIndex);
    onScrollToMessage(nextIndex);
  }, [activeIndex, msgCount, onScrollToMessage]);

  if (activeIndex >= msgCount) {
    setActiveIndex(activeIndex - 1);
    return null;
  }

  const cls = classNames(className, !isBulkUploadPreview && "scha__check-messages");
  const msg = t(`component.checkMessages.${msgType}`) || "";
  const buttonColor = msgType === "error" ? "error" : "inherit";

  if (msgCount === 0) {
    return null;
  }

  if (isBulkUploadPreview) {
    return (
      <div className={cls} {...divProps}>
        <AlertContainer className={"mr-auto"} severity={msgType}>
          <span>
            {`${msg} `}[{activeIndex + 1}/{msgCount}]
          </span>
          <ButtonContainer>
            <IconButton
              color={buttonColor}
              onClick={nextClick}
              disabled={msgCount !== 1 && activeIndex === msgCount - 1}
              size="small"
              sx={{ p: 0 }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
            <IconButton
              color={buttonColor}
              onClick={prevClick}
              disabled={msgCount !== 1 && activeIndex <= 0}
              size="small"
              sx={{ p: 0 }}
            >
              <KeyboardArrowUpIcon />
            </IconButton>
          </ButtonContainer>
        </AlertContainer>
      </div>
    );
  }

  return (
    <StickyAlert className={classNames("mr-auto", cls)} severity={msgType} sticky={sticky}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <span>{`${msg} [${activeIndex + 1}/${msgCount}`}]</span>
        <Box sx={{ display: "flex", my: -1 }}>
          <IconButton onClick={nextClick} disabled={msgCount !== 1 && activeIndex === msgCount - 1} color="inherit">
            <KeyboardArrowDownIcon />
          </IconButton>
          <IconButton onClick={prevClick} disabled={msgCount !== 1 && activeIndex <= 0} color="inherit">
            <KeyboardArrowUpIcon />
          </IconButton>
        </Box>
      </Box>
    </StickyAlert>
  );
};
export default CheckMessagesComponent;

const StickyAlert = styled(Alert, {
  shouldForwardProp: (prop) => prop !== "sticky",
})<{ sticky?: boolean }>(({ sticky }) => ({
  ...(sticky && {
    position: "sticky",
    top: 0,
    zIndex: 998,
  }),
}));

const AlertContainer = styled(Alert)({
  width: "100%",
  maxWidth: "unset",

  [`.MuiAlert-message`]: {
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
  },
});

const ButtonContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});
